import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  WebSocketService,
  SystemUpdate,
  SupportUpdate,
  SupportTicketUpdate,
  OperationsUpdate
} from './websocket.service';
import { SystemUserService } from './system-user.service';
import { UserService } from './user.service';

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warn' | 'error';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  category: 'system' | 'support' | 'operations' | 'security';
  actionUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private notificationSoundSubject = new Subject<void>();

  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public notificationSound$ = this.notificationSoundSubject.asObservable();

  // Tracked for support ticket events: never notify a user about something
  // they did themselves, and "assigned to you"/"replied on yours" checks.
  private currentUserId: number | null = null;

  constructor(
    private messageService: MessageService,
    private webSocketService: WebSocketService,
    private systemUserService: SystemUserService,
    private userService: UserService
  ) {
    this.userService.currentUser$.subscribe((user) => {
      this.currentUserId = user?.id ?? null;
    });
    // Only initialize WebSocket notifications for authenticated system users with appropriate roles
    if (this.systemUserService.isSystemUser()) {
      this.initializeWebSocketNotifications();
    }
  }

  private initializeWebSocketNotifications(): void {
    // Subscribe to system updates
    this.webSocketService.subscribeToSystemUpdates().subscribe((update) => {
      if (update) {
        this.handleSystemUpdate(update);
      }
    });

    // Subscribe to support updates
    // Plain Subject - no initial null to skip.
    this.webSocketService.subscribeToSupportUpdates().subscribe((update) => {
      this.handleSupportUpdate(update);
    });

    // Subscribe to operations updates
    this.webSocketService.subscribeToOperationsUpdates().subscribe((update) => {
      if (update) {
        this.handleOperationsUpdate(update);
      }
    });
  }

  private handleSystemUpdate(update: SystemUpdate): void {
    let notification: Notification | null = null;

    switch (update.entity) {
      case 'audit_log':
        if (update.action === 'created') {
          const data = update.data as any;
          notification = {
            id: `audit_${data?.id || Date.now()}`,
            type: 'info',
            title: 'New Audit Log Entry',
            message: `New audit log entry: ${
              data?.action_description || data?.action_type || 'Unknown action'
            }`,
            timestamp: new Date(),
            read: false,
            category: 'security',
            actionUrl: '/system-management/audit-logs'
          };
        }
        break;

      case 'task_execution':
        if (update.action === 'status_changed') {
          const data = update.data as any;
          const status = data?.success ? 'completed successfully' : 'failed';
          notification = {
            id: `task_${data?.id || Date.now()}`,
            type: data?.success ? 'success' : 'error',
            title: 'Task Execution Update',
            message: `Task "${data?.task_name || 'Unknown task'}" ${status}`,
            timestamp: new Date(),
            read: false,
            category: 'system',
            actionUrl: '/system-management/task-executions'
          };
        }
        break;

      case 'user':
        if (update.action === 'status_changed') {
          const data = update.data as any;
          const status = data?.enabled ? 'enabled' : 'disabled';
          notification = {
            id: `user_${data?.id || Date.now()}`,
            type: 'info',
            title: 'User Status Changed',
            message: `User "${data?.first_name || 'Unknown'} ${
              data?.last_name || 'User'
            }" has been ${status}`,
            timestamp: new Date(),
            read: false,
            category: 'system',
            actionUrl: '/system-management/users'
          };
        }
        break;

      case 'system_alert': {
        const data = update.data as any;
        notification = {
          id: `alert_${Date.now()}`,
          type: 'error',
          title: 'System Alert',
          message: data?.message || 'System alert received',
          timestamp: new Date(),
          read: false,
          category: 'system'
        };
        break;
      }
    }

    if (notification) {
      this.addNotification(notification);
    }
  }

  private handleSupportUpdate(update: SupportUpdate): void {
    if (update.entity !== 'ticket') return;
    const { data: event, timestamp } = update as SupportTicketUpdate;
    const ticket = event?.ticket;
    if (!ticket?.id) return;

    const me = this.currentUserId;
    // Never notify for events the current user caused.
    if (me !== null && event.actor_user_id === me) return;

    const ref = `#${ticket.id}${ticket.title ? ` - ${ticket.title}` : ''}`;
    // Stable per event (ticket + action + message id or envelope timestamp)
    // so the same event delivered twice doesn't stack duplicate entries.
    const eventKey = event.message?.id ?? timestamp ?? Date.now();
    let notification: Notification | null = null;

    switch (event.action) {
      case 'created':
        if (event.actor_type === 'customer' || event.actor_type === 'system') {
          notification = this.supportNotification(
            `support_created_${ticket.id}`,
            'New support ticket',
            ref,
            ticket.id
          );
        }
        break;

      case 'assigned':
        if (me !== null && ticket.assigned_to_user_id === me) {
          notification = this.supportNotification(
            `support_assigned_${ticket.id}_${eventKey}`,
            'Ticket assigned to you',
            ref,
            ticket.id
          );
        }
        break;

      case 'message_added':
        if (
          event.actor_type === 'customer' &&
          (ticket.assigned_to_user_id == null || (me !== null && ticket.assigned_to_user_id === me))
        ) {
          notification = this.supportNotification(
            `support_reply_${ticket.id}_${eventKey}`,
            'Customer replied',
            `Customer replied on ${ref}`,
            ticket.id
          );
        }
        break;

      case 'mentioned':
        notification = this.supportNotification(
          `support_mention_${ticket.id}_${eventKey}`,
          'You were mentioned',
          `You were mentioned on ${ref}`,
          ticket.id
        );
        break;
    }

    if (notification && !this.notificationsSubject.value.some((n) => n.id === notification?.id)) {
      this.addNotification(notification);
    }
  }

  private supportNotification(
    id: string,
    title: string,
    message: string,
    ticketId: number
  ): Notification {
    return {
      id,
      type: 'info',
      title,
      message,
      timestamp: new Date(),
      read: false,
      category: 'support',
      // Deep link - support-routing's activeTabMatcher opens the ticket.
      actionUrl: `/support/tickets/${ticketId}`
    };
  }

  private handleOperationsUpdate(update: OperationsUpdate): void {
    let notification: Notification | null = null;

    switch (update.entity) {
      case 'device':
        if (update.action === 'status_changed') {
          const data = update.data as any;
          const status = data?.status;
          notification = {
            id: `device_${data?.id || Date.now()}`,
            type: status === 'online' ? 'success' : 'warn',
            title: 'Device Status Changed',
            message: `Device "${data?.name || 'Unknown device'}" is now ${status}`,
            timestamp: new Date(),
            read: false,
            category: 'operations',
            actionUrl: '/operations/devices'
          };
        }
        break;

      case 'network':
        if (update.action === 'alert') {
          const data = update.data as any;
          notification = {
            id: `network_${Date.now()}`,
            type: 'error',
            title: 'Network Alert',
            message: data?.message || 'Network issue detected',
            timestamp: new Date(),
            read: false,
            category: 'operations',
            actionUrl: '/operations/network'
          };
        }
        break;

      case 'circuit':
        if (update.action === 'status_changed') {
          const data = update.data as any;
          notification = {
            id: `circuit_${data?.id || Date.now()}`,
            type: data?.status === 'active' ? 'success' : 'warn',
            title: 'Circuit Status Changed',
            message: `Circuit "${data?.name || 'Unknown circuit'}" status: ${data?.status}`,
            timestamp: new Date(),
            read: false,
            category: 'operations',
            actionUrl: '/operations/circuits'
          };
        }
        break;
    }

    if (notification) {
      this.addNotification(notification);
    }
  }

  private addNotification(notification: Notification): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = [notification, ...currentNotifications];

    // Keep only the latest 50 notifications
    if (updatedNotifications.length > 50) {
      updatedNotifications.splice(50);
    }

    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadCount();

    // Show toast notification
    this.showToastNotification(notification);

    // Play notification sound
    this.notificationSoundSubject.next();
  }

  private showToastNotification(notification: Notification): void {
    this.messageService.add({
      severity: notification.type,
      summary: notification.title,
      detail: notification.message,
      key: 'global'
    });
  }

  private updateUnreadCount(): void {
    const unreadCount = this.notificationsSubject.value.filter((n) => !n.read).length;
    this.unreadCountSubject.next(unreadCount);
  }

  // ============================================================================
  // LEGACY METHODS - Backward Compatibility
  // ============================================================================

  testToast() {
    this.messageService.add({
      key: 'main',
      severity: 'success',
      summary: 'Success',
      detail: 'Successfully tested a toast message'
    });
  }

  mainSuccess(message: string) {
    this.messageService.add({
      key: 'main',
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  mainError(message: string) {
    this.messageService.add({
      key: 'main',
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  mainWarning(message: string) {
    this.messageService.add({
      key: 'main',
      severity: 'warn',
      summary: 'Warning',
      detail: message
    });
  }

  mainInfo(message: string) {
    this.messageService.add({
      key: 'main',
      severity: 'info',
      summary: 'Info',
      detail: message
    });
  }

  successfullyModifiedEntity(action: string, entity: string) {
    let summary = '';
    if (action == 'create') {
      summary = 'Successfully added a record to ' + entity;
    } else if (action == 'update') {
      summary = 'Successfully updated a record of ' + entity;
    } else if (action == 'delete') {
      summary = 'Successfully deleted a record of ' + entity;
    }

    this.messageService.add({
      key: 'main',
      severity: 'success',
      summary: 'Success',
      detail: summary
    });
  }

  errorModifyingEntity(action: string, entity: string) {
    let summary = '';

    if (action == 'create') {
      summary = 'Failed to add a record to ' + entity;
    }
    if (action == 'update') {
      summary = 'Failed to update a record in ' + entity;
    }
    if (action == 'delete') {
      summary = 'Failed to delete a record of ' + entity;
    }

    this.messageService.add({
      key: 'main',
      severity: 'error',
      summary: 'Error',
      detail: summary
    });
  }

  successfulQuotation() {
    this.messageService.add({
      key: 'business',
      severity: 'success',
      summary: 'Success',
      detail: 'Successfully submitted a new quotation request'
    });
  }

  failedQuotation() {
    this.messageService.add({
      key: 'business',
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to submit a new quotation request'
    });
  }

  invalidFields() {
    this.messageService.add({
      key: 'business',
      severity: 'error',
      summary: 'Error',
      detail: 'Please fill in all required fields'
    });
  }

  successfullPortal() {
    this.messageService.add({
      key: 'portal',
      severity: 'success',
      summary: 'Success',
      detail: 'Successfully created a new portal'
    });
  }

  failedPortal() {
    this.messageService.add({
      key: 'portal',
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create a new portal'
    });
  }

  successfulCredentialCopy() {
    this.messageService.add({
      key: 'portal',
      severity: 'success',
      summary: 'Copied Credentials',
      detail: 'Successfully copied credentials to the clipboard'
    });
  }

  // ============================================================================
  // NEW WEB SOCKET NOTIFICATION METHODS
  // ============================================================================

  // Public methods for WebSocket notifications
  public getNotifications(): Observable<Notification[]> {
    return this.notifications$;
  }

  public getUnreadCount(): Observable<number> {
    return this.unreadCount$;
  }

  public markAsRead(notificationId: string): void {
    const notifications = this.notificationsSubject.value;
    const updatedNotifications = notifications.map((n) =>
      n.id === notificationId ? { ...n, read: true } : n
    );
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadCount();
  }

  public markAllAsRead(): void {
    const notifications = this.notificationsSubject.value;
    const updatedNotifications = notifications.map((n) => ({ ...n, read: true }));
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadCount();
  }

  public deleteNotification(notificationId: string): void {
    const notifications = this.notificationsSubject.value;
    const updatedNotifications = notifications.filter((n) => n.id !== notificationId);
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadCount();
  }

  public clearAllNotifications(): void {
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
  }

  public addManualNotification(
    notification: Omit<Notification, 'id' | 'timestamp' | 'read'>
  ): void {
    const newNotification: Notification = {
      ...notification,
      id: `manual_${Date.now()}`,
      timestamp: new Date(),
      read: false
    };
    this.addNotification(newNotification);
  }

  public getNotificationSound(): Observable<void> {
    return this.notificationSound$;
  }

  // ============================================================================
  // STANDARDIZED NOTIFICATION METHODS
  // ============================================================================

  /**
   * Standardized method for showing success notifications
   */
  public showSuccess(title: string, message: string, key: string = 'global'): void {
    this.messageService.add({
      severity: 'success',
      summary: title,
      detail: message,
      key: key
    });
  }

  /**
   * Standardized method for showing error notifications
   */
  public showError(title: string, message: string, key: string = 'global'): void {
    this.messageService.add({
      severity: 'error',
      summary: title,
      detail: message,
      key: key
    });
  }

  /**
   * Standardized method for showing warning notifications
   */
  public showWarning(title: string, message: string, key: string = 'global'): void {
    this.messageService.add({
      severity: 'warn',
      summary: title,
      detail: message,
      key: key
    });
  }

  /**
   * Standardized method for showing info notifications
   */
  public showInfo(title: string, message: string, key: string = 'global'): void {
    this.messageService.add({
      severity: 'info',
      summary: title,
      detail: message,
      key: key
    });
  }

  /**
   * Standardized method for entity operations
   */
  public showEntitySuccess(
    action: 'create' | 'update' | 'delete',
    entity: string,
    key: string = 'main'
  ): void {
    const actionText =
      action === 'create' ? 'created' : action === 'update' ? 'updated' : 'deleted';
    this.messageService.add({
      key: key,
      severity: 'success',
      summary: 'Success',
      detail: `Successfully ${actionText} ${entity}`
    });
  }

  /**
   * Standardized method for entity operation errors
   */
  public showEntityError(
    action: 'create' | 'update' | 'delete',
    entity: string,
    key: string = 'main'
  ): void {
    const actionText = action === 'create' ? 'create' : action === 'update' ? 'update' : 'delete';
    this.messageService.add({
      key: key,
      severity: 'error',
      summary: 'Error',
      detail: `Failed to ${actionText} ${entity}`
    });
  }

  /**
   * Pulls a human-readable message out of an HttpErrorResponse, checking
   * every shape the backend actually uses (FastAPI's {detail: "..."} is the
   * standard one; {message: "..."} covers a handful of older endpoints).
   * Falls back to the caller-supplied message only when the response body
   * has neither - never shows raw JSON or "[object Object]" to a user.
   */
  public getApiErrorDetail(err: unknown, fallbackMessage: string): string {
    const body = (err as { error?: unknown } | null)?.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      const detail = (body as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      const message = (body as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    return fallbackMessage;
  }

  /**
   * The one place a failed HTTP call should render its error - surfaces the
   * backend's actual detail message instead of a generic canned string,
   * while still guaranteeing a sensible fallback when the response carries
   * none (a network failure, a 500 with no body, etc.).
   *
   * Usage: `.subscribe({ error: (err) => this.ns.showApiError(err, 'Failed to reactivate the service.') })`
   */
  public showApiError(
    err: unknown,
    fallbackMessage: string,
    title: string = 'Error',
    key: string = 'main'
  ): void {
    this.messageService.add({
      key,
      severity: 'error',
      summary: title,
      detail: this.getApiErrorDetail(err, fallbackMessage)
    });
  }
}
